<template>
	<div class="tab-pane" :class="{ active: defaultTab, show: defaultTab }" :id="id">
		<div class="p-4 b-b _600 nav-title">Scoring Setup</div>
		<div class="row m-0">
			<div class="px-4 pt-4 col-sm-6">
				<div class="form-group" :class="{ invalid: !valid.field('rubric') }">
					<label>Rubric</label>
					<v-select
						class="v-select-apply"
						:options="rubrics"
						v-model="item.rubric"
						@input="updateRubricID"
						label="name"
						:disabled="item.item.isScored"
					></v-select>
				</div>
				<div class="form-group" :class="{ invalid: !valid.field('type') }">
					<label>Item Type</label>
					<config-select
						:options="item_types"
						byField="id"
						v-model="item.type"
						:disabled="item.isScored"
					></config-select>
				</div>
				<div class="form-group row mx-0 align-bottom" v-if="item.type == 'DOUBLE_WEIGHTED'">
					<div class="p-0 col-4 mt-auto">
						<label>Max Weight Diff.</label>
						<input type="number" class="form-control" v-model.number="item.item.weighted_resolution_threshold" />
					</div>
					<div class="p-0 pl-2 col-4 mt-auto">
						<label>Range Min</label>
						<input type="number" class="form-control" v-model.number="item.item.weighted_resolution_range_min" />
					</div>
					<div class="p-0 pl-2 col-4 mt-auto">
						<label>Range Max</label>
						<input type="number" class="form-control" v-model.number="item.item.weighted_resolution_range_max" />
					</div>
				</div>
				<div class="form-group row mx-0 align-bottom" v-show="item.type == 'OMAHA'">
					<div class="p-0 col-4 mt-auto checkbox">
						<label class="ui-check">
							<input type="checkbox" v-model="item.item.omaha_min_max" />
							<i class="dark-white"></i> Resolve Min/Max
						</label>
					</div>
					<div class="p-0 pl-2 col-4 mt-auto">
						<label>Max Non-adjacent</label>
						<input type="number" class="form-control" v-model.number="item.item.omaha_max_non_adj" />
					</div>
					<div class="p-0 pl-2 col-4 mt-auto">
						<label>Max Score Diff</label>
						<input type="number" class="form-control" v-model.number="item.item.omaha_max_weighted_score_diff" />
					</div>
				</div>
				<div class="form-group row mx-0 align-bottom" v-if="!noRes">
					<div class="p-0 col-4 mt-auto">
						<label v-if="!item.item.per_class_reliability">Reliability %</label>
						<label v-if="item.item.per_class_reliability">Default Reliability %</label>
						<percent-input v-model="item.item.reliability_percent" />
					</div>
					<div
						class="col-8 mt-auto d-flex flex-column justify-content-end pr-0"
					>
						<button
							v-if="dirty"
							class="btn btn-secondary btn-block"
							disabled
							aria-expanded="false"
							v-tippy="{ theme: 'popover', placement: 'right-start' }"
							title="You must save the item before you can regenerate reliability"
						>
							Regenerate Reliability...
						</button>
						<button
							v-if="!dirty"
							class="btn theme-accent btn-block"
							data-toggle="dropdown"
							aria-expanded="false"
							@click="regenReliability(item.id)"

							v-tippy="{ theme: 'popover', placement: 'right-start' }"
							title="<p>Reliability (the responses marked for second scoring) is usually calculated as responses are uploaded.</p><p>Use this action to re-calculate which responses should be second-scored.</p><p><small>Responses that have already received second scores will not have them removed, so setting reliability to a lower percentage during or after scoring may not always work.</small></p>"
						>
							Regenerate Reliability...
						</button>
					
					</div>
				</div>
				<div class="form-group row mx-0 align-bottom" v-if="!noRes">
					<div class="p-0 col-4 mt-auto">
						<label
							v-tippy="{ theme: 'popover', placement: 'right-start' }"
							title="Second and Resolution Scorers will not be allowed to score more than this percentage of available responses, unless the total number of outstanding responses is less than 10<br/><br/>Set to <b>0</b> to disable"
							>Individual Limit %</label
						>
						<percent-input v-model="item.item.individual_limit_percent" />
					</div>
					<div class="col-8 mt-auto d-flex flex-column justify-content-end pr-0">
						<!--
						<button
							class="btn theme-accent btn-block"
							@click="simRubric.show = !simRubric.show"
							:disabled="!valid.field('rubric')"
						>
							Simulate Resolution Rule
							<i
								class="far fa-fw fa-angle-up anim-rotate"
								:class="{'rotate-180': simRubric.show}"
							></i>
						</button>
					-->
					</div>
				</div>
			</div>
			<div class="px-4 pt-4 col-sm-6">
				<p>Scoring Options</p>
				<div class="checkbox">
					<label class="ui-check" :class="{ 'text-muted': item.item.isScored }">
						<input v-model="item.item.resolution_choose_score" type="checkbox" :disabled="item.item.isScored" />
						<i class="dark-white"></i> Choose Keep Score
					</label>
				</div>
				<div class="checkbox">
					<label
						class="ui-check"
						v-tippy="{ theme: 'popover', placement: 'left-start' }"
						title="Users cannot submit a score until they have viewed the entire response"
					>
						<input v-model="item.item.require_view_all" type="checkbox" />
						<i class="dark-white"></i> Require to View Whole Response
					</label>
				</div>
				<div class="checkbox">
					<label
						class="ui-check"
						v-tippy="{ theme: 'popover', placement: 'left-start' }"
						title="Users cannot see previous scores during resolution"
					>
						<input v-model="item.item.resolution_blind" type="checkbox" />
						<i class="dark-white"></i> Blind Resolution
					</label>
				</div>
				<div class="checkbox">
					<label class="ui-check">
						<input v-model="item.item.flag_scoring_enabled" type="checkbox" />
						<i class="dark-white"></i> Flag Queue Scoring
					</label>
				</div>
				<div class="checkbox">
					<label class="ui-check">
						<input v-model="item.item.reset_on_review" type="checkbox" />
						<i class="dark-white"></i> Reset Scores on Review
					</label>
				</div>
				<div class="checkbox" v-if="!singleScore">
					<label
						class="ui-check"
						v-tippy="{ theme: 'popover', placement: 'left-start' }"
						title="Responses can be marked individually, from Auditing, to receive second scores"
					>
						<input v-model="item.item.allow_manual_reliability" type="checkbox" />
						<i class="dark-white"></i> Allow Manual Reliability
					</label>
				</div>
				<div class="checkbox">
					<label
						class="ui-check"
						v-tippy="{ theme: 'popover', placement: 'left-start' }"
						title="Responses to receive second scores will be calculated as a percentage of each individual class, rather than a percentage of the whole response pool"
					>
						<input v-model="item.item.per_class_reliability" type="checkbox" />
						<i class="dark-white"></i> Per-Class Reliability
					</label>
				</div>
				<div class="checkbox" v-if="item.item.per_class_reliability">
					<label
						class="ui-check ml-4"
						v-tippy="{ theme: 'popover', placement: 'left-start' }"
						title="The percentage of responses to receive second scores within each class can be changed by the class's teacher"
					>
						<input v-model="item.item.allow_teacher_reliability" type="checkbox" />
						<i class="dark-white"></i> Allow Teacher Configuration
					</label>
				</div>

				<hr class="mt-2" />

				<div class="checkbox">
					<label class="ui-check">
						<input v-model="item.item.use_considerations" type="checkbox" />
						<i class="dark-white"></i> Use Scoring Considerations
					</label>
				</div>
				<div class="checkbox">
					<label class="ui-check">
						<input v-model="item.item.use_comments" type="checkbox" />
						<i class="dark-white"></i> Use Inter-scorer Comments
					</label>
				</div>
				<div class="checkbox">
					<label class="ui-check">
						<input v-model="item.item.use_feedback" type="checkbox" />
						<i class="dark-white"></i> Use Student Feedback Comments
					</label>
				</div>
			</div>
			<div class="px-4" v-if="valid.field('rubric') && !noRes" v-show="simRubric.show">
				<div class="pr-4" style="display: inline-block; vertical-align: top;">
					<rubric
						:outer-rubric="item.rubric"
						:score.sync="simRubric.leftScore"
						:complete.sync="simRubric.leftComplete"
						:reset="simRubric.leftReset"
					></rubric>
					<div class="my-2" v-if="simRubric.leftWeight != null">
						Weighted Score:
						<span class="_600 text-theme" v-if="!simRubric.leftLoading">{{ simRubric.leftWeight }}</span>
						<loading v-if="simRubric.leftLoading" type="icon" />
					</div>
				</div>
				<div style="display: inline-block; vertical-align: top;" v-if="shouldCompareRubrics(item.type)">
					<rubric
						:outer-rubric="item.rubric"
						:score.sync="simRubric.rightScore"
						:complete.sync="simRubric.rightComplete"
						:reset="simRubric.rightReset"
					></rubric>
					<div class="my-2" v-if="simRubric.rightWeight != null">
						Weighted Score:
						<span class="_600 text-theme" v-if="!simRubric.rightLoading">{{ simRubric.rightWeight }}</span>
						<loading v-if="simRubric.rightLoading" type="icon" />
					</div>
				</div>
				<div class="w-100 pb-4">
					<div>
						Needs Resolution:
						<span v-if="!simRubric.resLoading && simRubric.needsRes == null" class="_600 text-theme"
							>?</span
						>
						<span v-if="!simRubric.resLoading && simRubric.needsRes == true" class="_600 text-theme"
							>Yes</span
						>
						<span v-if="!simRubric.resLoading && simRubric.needsRes == false" class="_600 text-theme"
							>No</span
						>
						<loading v-if="simRubric.resLoading" type="icon" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.invalid {
	color: red;
}
.v-select-apply .dropdown-toggle {
	border-radius: 0px;
	/* should work but doesn't :( */
}
.anim-rotate {
	transition: transform 0.3s ease-out;
}
.rotate-180 {
	transform: rotate(180deg);
}
button {
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>

<script>
import RubricService from "@/services/RubricService";
import ItemService from "@/services/ItemService";
import notie from "@/services/NotieService";

import PercentInput from "@/components/PercentInput";
import ConfigSelect from "@/components/ConfigSelect";
import Rubric from "@/components/Rubric";

export default {
	name: "ItemEditScoring",

	props: ["id", "item", "dirty", "defaultTab", "validationHints", "valid","template"],

	components: { PercentInput, ConfigSelect, Rubric },

	data() {
		return {
			rubrics: [],
			isScored: false,
			item_types: [
				{
					id: "SINGLE",
					name: "Single Scored"
				},
				{
					id: "DOUBLE_ONLY",
					name: "Double Scored No Resolution"
				},
				{
					id: "DOUBLE_EXACT",
					name: "Double Scored Exact"
				},
				{
					id: "DOUBLE_EXACT_ADJACENT",
					name: "Double Scored Exact/Adjacent"
				},
				{
					id: "DOUBLE_WEIGHTED",
					name: "Double Scored Weighted Range"
				},
				{
					id: "OMAHA",
					name: "Omaha Scoring Rule"
				}
			],
			simRubric: {
				show: false,
				leftScore: {},
				leftComplete: null,
				rightScore: {},
				rightComplete: null,
				leftWeight: null,
				rightWeight: null,
				needsRes: null,
				leftLoading: false,
				rightLoading: false,
				resLoading: false,
				leftReset: 0,
				rightReset: 0
			},
			assessments: null
		};
	},

	created() {

		if(!this.template){
			RubricService.listAllRubrics()
				.then(resp => {
					this.rubrics = resp.data.rubrics;
				})
				.catch(() => {
					notie.error("Failed to load rubrics", err);
				});
		}else{
			RubricService.listAllMasterRubrics()
				.then(resp => {
					this.rubrics = resp.data.rubrics;
				})
				.catch(() => {
					notie.error("Failed to load rubrics", err);
				});
		}


		this.loadAssessments();
	},

	computed: {
		singleScore() {
			return;
			this.item && this.item.type == "SINGLE";
		},
		noRes() {
			return this.item && (this.item.type == "SINGLE" || this.item.type == "DOUBLE_ONLY");
		}
	},

	watch: {
		"item.rubric"() {
			this.clearSim();
		},
		"item.type"() {
			this.clearSim();
		},
		"item.weighted_resolution_threshold"() {
			this.checkSim();
		},
		"item.weighted_resolution_range_min"() {
			this.checkSim();
		},
		"item.weighted_resolution_range_max"() {
			this.checkSim();
		},
		"item.omaha_min_max"() {
			this.checkSim();
		},
		"item.omaha_max_non_adj"() {
			this.checkSim();
		},
		"item.omaha_max_weighted_score_diff"() {
			this.checkSim();
		},
		"simRubric.leftScore"() {
			if (this.simRubric.leftComplete) {
				this.simRubric.leftLoading = true;
				this.checkSim();
			} else {
				this.simRubric.needsRes = null;
			}
		},
		"simRubric.rightScore"() {
			if (this.simRubric.rightComplete) {
				this.simRubric.rightLoading = true;
				this.checkSim();
			} else {
				this.simRubric.needsRes = null;
			}
		},
		"item.id"() {
			this.loadAssessments();
		}
	},

	methods: {
		loadRubrics(search) {
				return RubricService.listRubrics();
		},

		loadAssessments() {
			if (!this.item.id) {
				return;
			}
			ItemService.getItemAssessments(this.item.id)
				.then(resp => {
					this.assessments = resp.data;
				})
				.catch(e => {
					console.log(e);
					Notie.error("Failed to load assessments using item", e);
				});
		},

		updateRubricID(rubric) {
			this.item.rubric_id = (rubric && rubric.id) || "";
		},

		shouldCompareRubrics(scoringRule) {
			switch (scoringRule) {
				case "SINGLE":
					return false;
				case "DOUBLE_ONLY":
					return true;
				case "DOUBLE_EXACT":
					return true;
				case "DOUBLE_EXACT_ADJACENT":
					return true;
				case "DOUBLE_WEIGHTED":
					return true;
				case "OMAHA":
					return true;
				case "THREE_READS":
					return true;
			}
		},

		clearSim() {
			console.log("=== CLEARING SIM ===");
			this.simRubric.leftReset++;
			this.simRubric.rightReset++;
			this.simRubric.leftComplete = null;
			this.simRubric.rightComplete = null;
			this.simRubric.leftWeight = null;
			this.simRubric.rightWeight = null;
			this.simRubric.needsRes = null;
			this.simRubric.leftLoading = false;
			this.simRubric.rightLoading = false;
			this.simRubric.resLoading = false;
		},

		checkSim() {
			if (!(this.item.rubric && this.item.type && this.simRubric.show)) {
				return;
			}

			let first = this.simRubric.leftComplete ? this.simRubric.leftScore : null;
			let second = this.simRubric.rightComplete ? this.simRubric.rightScore : null;

			this.simRubric.resLoading = true;
			ItemService.simScorePair(this.item, first, second)
				.then(resp => {
					if (resp.data.has_weight) {
						if (this.simRubric.leftComplete) {
							this.simRubric.leftWeight = resp.data.first_weight;
						}
						if (this.simRubric.rightComplete) {
							this.simRubric.rightWeight = resp.data.second_weight;
						}
					}
					if (resp.data.compared) {
						this.simRubric.needsRes = resp.data.needsRes;
					}
				})
				.catch(err => {
					console.log(err);
					notie.error("Failed to simulate scores", err);
				})
				.finally(() => {
					this.simRubric.leftLoading = false;
					this.simRubric.rightLoading = false;
					this.simRubric.resLoading = false;
				});
		},

		regenReliability(asmtID) {
			ItemService.regenerateReliability(asmtID)
				.then(() => {
					notie.success("Regenerated responses marked for second scoring");
				})
				.catch(e => {
					console.log(e);
					notie.error("Failed to regenerate reliability", e);
				});
		}
	}
};
</script>



// WEBPACK FOOTER //
// src/components/item/ItemEditScoring.vue